import { MessageDialogService } from 'src/app/shared/component/message-dialog/message-dialog.service';
import { filter, map, pairwise, startWith, Subject, SubscriptionLike, takeUntil, throttleTime } from 'rxjs';
import { NotificationService } from '../../shared/service/notification.service';
import { Component, ElementRef, HostListener,  OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { trigger, transition, style, animate } from '@angular/animations';
import { NotificationAction, NotificationModel } from './model/notification.model';
import TimeAgo from 'javascript-time-ago';
import { formatDate } from '@angular/common';
import { NotificationActionHelper } from './util/actions-handler.util';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NotificationSoundHelper } from './util/notification-sount.util';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  animations: [
    trigger('transformMenu', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }), // Estado inicial (invisível e um pouco acima)
        animate('100ms cubic-bezier(0, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' }) // Estado final
        ),
      ]),
      transition(':leave', [
        animate('150ms linear', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class NotificationComponent implements OnDestroy {

    @ViewChild('trigger') trigger!: ElementRef;
    @ViewChild('menuTemplate') menuTemplate!: TemplateRef<any>;
    @ViewChild(CdkVirtualScrollViewport) set viewportSetter(viewport: CdkVirtualScrollViewport) {
        this.handleScrollViewPort(viewport);
    }

    private destroyViewport$ = new Subject<void>();
    private readonly sseSubscription: SubscriptionLike;

    overlayRef!: OverlayRef;
    notifications: NotificationModel[];

    loading = false;
    timeAgo = new TimeAgo('pt-BR');
    keepFetching = true;
    readType: 'all' | 'unread' = 'all';
    unreadCount = 0;

    constructor(
        private notificationService: NotificationService,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef,
        protected dialogService: MessageDialogService,
        private actionHelper: NotificationActionHelper,
        private soundHelper: NotificationSoundHelper
    ) {
        this.loading = true;

        this.sseSubscription = this.notificationService.connectToSSEServer()
            .subscribe({
                    next: (msg) => {
                        if (!msg || typeof msg === 'string') return;
                        this.addNotification(msg);
                    },
                    error: (error) => {
                        console.error(error);
                    }
                }
            );

        this.notificationService.getNotifications({ limit: 10, skip: 0 }).subscribe(result => {
            this.notifications = result.map(elem => this.formatNotification(elem));
            this.unreadCount = this.notifications.filter(elem => !elem.read).length;
            this.loading = false;
        })

    }
    ngOnDestroy() {
        this.unsubscribeSSeService();
    }
    public eventOpenOptions(): void {
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.trigger)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 0,
                    offsetX: -20,
                }
            ]);
        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });

        this.overlayRef.backdropClick().subscribe(() => { this.overlayRef.dispose();});

        const menuTemplateBuilt = new TemplatePortal(this.menuTemplate, this.viewContainerRef);

        this.overlayRef.attach(menuTemplateBuilt);
    }

    public formatTimeAgo(date: string) {
        return this.timeAgo.format(new Date(date), 'round-minute')
    }
    public eventCloseOverlay(): void {
        this.overlayRef?.dispose();
    }
    public eventRemoveNotification(notificationId: string) {
        this.dialogService.openConfirmation('Remover notificação').subscribe(
            (result) => {
                if (result.confirmed) {
                    this.notificationService.deleteNotification(notificationId).subscribe(
                        () => {
                           this.notifications = this.notifications.filter(n => n._id !== notificationId);
                        }
                    );
                }
            }
        );
    }
    public eventMarkNotificationAsRead(notificationId: string) {
        const isRead = this.notifications.find(n => n._id === notificationId)?.read;
        if (isRead) return;

        this.notificationService.markNotificationAsRead(notificationId).subscribe(
            () => {
                this.notifications = this.notifications.map(n =>
                    n._id === notificationId ? { ...n, read: true } : n
                );

                if (this.unreadCount > 0) this.unreadCount--;
            }
        );
    }
    public addNotification(notification: NotificationModel) {
        this.unreadCount++;
        this.notifications = [this.formatNotification(notification), ...this.notifications];
        this.soundHelper.play()
    }
    public eventHandleAction(action?: NotificationAction) {
        if (!action) return;

        this.actionHelper.handle(action);
    }
    private formatNotification(notification: NotificationModel) {
        notification.createdAt = formatDate(notification.createdAt, 'medium', 'en-US')
        notification.timeAgo = this.formatTimeAgo(notification.createdAt);

        return notification;
    }
    private onScrollEnd() {
        if (!this.loading && this.keepFetching) {
            this.loading = true;
            this.notificationService.getNotifications({ limit: 10, skip: this.notifications.length }).subscribe(result => {
                if (!result.length) {
                    this.keepFetching = false;
                    this.loading = false;
                    return
                }

                this.notifications = [...this.notifications, ...result.map(elem => this.formatNotification(elem))];
                this.unreadCount = this.notifications.filter(elem => !elem.read).length;
                this.loading = false;
            })
        }
    }
    private handleScrollViewPort(viewport: CdkVirtualScrollViewport) {
        if (viewport) {
            this.destroyViewport$.next();

            viewport.elementScrolled()
                .pipe(
                    takeUntil(this.destroyViewport$),
                    filter(() => !this.loading),
                    map(() => viewport.measureScrollOffset('top')),
                    startWith(0),
                    pairwise(),
                    filter(([prev, curr]) => {
                        const isScrollingDown = curr > prev;
                        const isAtBottom = viewport.measureScrollOffset('bottom') < 10; // 10px de margem
                        return isScrollingDown && isAtBottom;
                    }),
                    throttleTime(1000, undefined, { leading: true, trailing: false })
                )
                .subscribe(() => {
                    this.onScrollEnd();
                });
        } else {
            this.destroyViewport$.next();
        }
    }
    @HostListener('window:beforeunload')
    unsubscribeSSeService() {
        this.sseSubscription.unsubscribe();
    }
}
